import React, { useEffect, useMemo, useState } from 'react';
import Topbar from '../Components/Topbar';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAdminDashboard } from '../Config/dashboardService';
import { formatCentsAsCurrency } from '../Utils/money';

const EMPTY_ARRAY = [];
const TIMEZONE_OPTIONS = [
  { label: 'Canada (Toronto / GTA)', value: 'America/Toronto' },
  { label: 'India (IST)', value: 'Asia/Kolkata' },
  { label: 'USA (New York)', value: 'America/New_York' },
  { label: 'USA (Chicago)', value: 'America/Chicago' },
  { label: 'USA (Denver)', value: 'America/Denver' },
  { label: 'USA (Los Angeles)', value: 'America/Los_Angeles' },
];

function formatYYYYMMDDCompact(value) {
  if (!value || typeof value !== 'string') return '';
  // "YYYY-MM-DD" -> "MM-DD" (tighter x-axis labels on small screens)
  if (value.length === 10 && value[4] === '-' && value[7] === '-') return value.slice(5);
  return value;
}

function toYYYYMMDD(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function KpiCard({ title, value, subtitle, accentClassName = 'from-indigo-600 to-violet-600' }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
      <div className={`h-1 bg-gradient-to-r ${accentClassName}`} />
      <div className="p-4 sm:p-5">
        <div className="text-sm font-medium text-gray-600">{title}</div>
        <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
        {subtitle ? <div className="mt-1 text-xs text-gray-500">{subtitle}</div> : null}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, right }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex items-start justify-between gap-4 p-4 sm:p-5 border-b border-gray-100">
        <div>
          <div className="text-base font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-gray-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

function Home() {
  const [tz, setTz] = useState(() => {
    return 'America/New_York';
  });

  const [useCustomRange, setUseCustomRange] = useState(false);
  const [days, setDays] = useState(30);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [appliedParams, setAppliedParams] = useState({ days: 30, tz: 'America/New_York' });
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getAdminDashboard(appliedParams);
        if (cancelled) return;
        if (!res?.success) {
          throw new Error(res?.message || 'Failed to load dashboard');
        }
        setDashboard(res.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [appliedParams, reloadNonce]);

  const financeDaily = dashboard?.series?.finance_daily ?? EMPTY_ARRAY;
  const ordersByStatusDaily = dashboard?.series?.orders_by_status_daily ?? EMPTY_ARRAY;
  const supportDaily = dashboard?.series?.support_daily ?? EMPTY_ARRAY;

  const financeChartData = useMemo(() => {
    return financeDaily.map((row) => ({
      date: row.date,
      gmv: Number(row.invoice_total_amount || 0) / 100,
      revenue: Number(row.platform_commission_amount || 0) / 100,
      orders: Number(row.completed_orders || 0),
    }));
  }, [financeDaily]);

  const ordersStatuses = useMemo(() => {
    const set = new Set();
    for (const row of ordersByStatusDaily) set.add(row.status);
    return Array.from(set).sort();
  }, [ordersByStatusDaily]);

  const ordersChartData = useMemo(() => {
    const byDate = new Map();
    for (const row of ordersByStatusDaily) {
      if (!byDate.has(row.date)) byDate.set(row.date, { date: row.date });
      const obj = byDate.get(row.date);
      obj[row.status] = Number(row.count || 0);
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [ordersByStatusDaily]);

  const supportChartData = useMemo(() => {
    return supportDaily.map((row) => ({
      date: row.date,
      newTickets: Number(row.new_tickets || 0),
      resolvedTickets: Number(row.resolved_tickets || 0),
      activeTickets: Number(row.active_tickets_end_of_day || 0),
    }));
  }, [supportDaily]);

  const rangeLabel = dashboard?.range
    ? `${dashboard.range.from} → ${dashboard.range.to} (${dashboard.range.tz || 'UTC'})`
    : '';

  const applyFilters = () => {
    const next = { tz: tz || 'UTC' };
    if (useCustomRange && fromDate && toDate) {
      next.from = toYYYYMMDD(fromDate);
      next.to = toYYYYMMDD(toDate);
    } else {
      next.days = Math.max(1, Math.min(365, Number(days || 30)));
    }
    setAppliedParams(next);
  };

  const cards = dashboard?.cards;
  const today = cards?.today || {};
  const totals = cards?.totals || {};
  const pending = cards?.pending || {};
  const supportTotalsByStatus = cards?.support?.totals_by_status || [];

  const statusColors = [
    'bg-indigo-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-violet-500',
    'bg-slate-500',
  ];

  const ordersBarColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#64748b'];

  return (
    <div className="min-h-screen bg-slate-50">
      <Topbar />

      <main className="max-w-7xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-2xl font-semibold text-gray-900">Admin Dashboard</div>
            <div className="mt-1 text-sm text-gray-500">
              {rangeLabel ? (
                <>
                  Analytics for <span className="font-medium text-gray-700">{rangeLabel}</span>
                </>
              ) : (
                'KPIs and trends'
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => setReloadNonce((n) => n + 1)}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
            >
              Refresh
            </button>
            <button
              onClick={applyFilters}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-[#EF5E28] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#d95325]"
            >
              Apply
            </button>
          </div>
        </div>

        <Panel
          title="Filters"
          subtitle="Use a preset range or set a custom date window. Amounts are returned in cents."
          right={
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-[#EF5E28] focus:ring-[#EF5E28]"
                checked={useCustomRange}
                onChange={(e) => setUseCustomRange(e.target.checked)}
              />
              Custom range
            </label>
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Preset</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {[7, 30, 90, 365].map((d) => (
                  <button
                    key={d}
                    onClick={() => {
                      setUseCustomRange(false);
                      setDays(d);
                      setAppliedParams({ days: d, tz: tz || 'UTC' });
                    }}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold ring-1 ${
                      !useCustomRange && Number(days) === d
                        ? 'bg-[#EF5E28] text-white ring-[#EF5E28]'
                        : 'bg-white text-gray-800 ring-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Last {d} days
                  </button>
                ))}
              </div>
              <div className="mt-3 text-xs text-gray-500">
                Tip: presets auto-load immediately; custom range loads when you hit Apply.
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom range</div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">From</div>
                  <DatePicker
                    selected={fromDate}
                    onChange={(d) => setFromDate(d)}
                    disabled={!useCustomRange}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002F53] disabled:bg-gray-50"
                    placeholderText="YYYY-MM-DD"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">To</div>
                  <DatePicker
                    selected={toDate}
                    onChange={(d) => setToDate(d)}
                    disabled={!useCustomRange}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002F53] disabled:bg-gray-50"
                    placeholderText="YYYY-MM-DD"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Timezone</div>
              <select
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002F53]"
              >
                {TIMEZONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="mt-2 text-xs text-gray-500">
                Used for daily grouping (Canada uses Toronto / GTA timezone).
              </div>
            </div>
          </div>
        </Panel>

        {error ? (
          <div className="rounded-2xl bg-rose-50 ring-1 ring-rose-200 p-5 text-rose-800">
            <div className="font-semibold">Couldn’t load dashboard</div>
            <div className="mt-1 text-sm">{error}</div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <KpiCard
            title="GMV (Today)"
            value={formatCentsAsCurrency(today.invoice_total_amount, 'CAD')}
            subtitle="Total invoice amount today"
            accentClassName="from-indigo-600 to-blue-600"
          />
          <KpiCard
            title="Platform Revenue (Today)"
            value={formatCentsAsCurrency(today.platform_commission_amount, 'CAD')}
            subtitle="Commission earned today"
            accentClassName="from-emerald-600 to-teal-600"
          />
          <KpiCard
            title="Completed Orders (Today)"
            value={Number(today.completed_orders || 0).toLocaleString()}
            subtitle="Orders completed today"
            accentClassName="from-amber-500 to-orange-600"
          />
          <KpiCard
            title="GMV (Range)"
            value={formatCentsAsCurrency(totals.invoice_total_amount, 'CAD')}
            subtitle="Total invoice amount in range"
            accentClassName="from-indigo-500 to-violet-600"
          />
          <KpiCard
            title="Platform Revenue (Range)"
            value={formatCentsAsCurrency(totals.platform_commission_amount, 'CAD')}
            subtitle="Commission in range"
            accentClassName="from-emerald-500 to-lime-600"
          />
          <KpiCard
            title="Completed Orders (Range)"
            value={Number(totals.completed_orders || 0).toLocaleString()}
            subtitle="Orders completed in range"
            accentClassName="from-sky-500 to-cyan-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel title="Pending queue" subtitle="Items needing review or action">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Charges', value: pending.charges, color: 'from-fuchsia-600 to-violet-600' },
                { label: 'Quotations', value: pending.quotations, color: 'from-indigo-600 to-sky-600' },
                { label: 'User verifications', value: pending.user_verifications, color: 'from-amber-500 to-orange-600' },
                { label: 'Documents', value: pending.documents, color: 'from-emerald-600 to-teal-600' },
              ].map((x) => (
                <div key={x.label} className="rounded-2xl bg-white ring-1 ring-gray-200 overflow-hidden">
                  <div className={`h-1 bg-gradient-to-r ${x.color}`} />
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{x.label}</div>
                    <div className="mt-2 text-2xl font-semibold text-gray-900">
                      {Number(x.value || 0).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Support overview" subtitle="Tickets totals by status">
            {supportTotalsByStatus.length ? (
              <div className="space-y-3">
                {supportTotalsByStatus.map((row, idx) => (
                  <div key={`${row.status}-${idx}`} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`h-2.5 w-2.5 rounded-full ${statusColors[idx % statusColors.length]}`} />
                      <div className="text-sm font-medium text-gray-800">{row.status}</div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">{Number(row.count || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No support data in the selected range.</div>
            )}
          </Panel>

          <Panel title="Data health" subtitle="Quick sanity checks">
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Finance points</span>
                <span className="font-semibold">{financeDaily.length.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Orders status points</span>
                <span className="font-semibold">{ordersByStatusDaily.length.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Support points</span>
                <span className="font-semibold">{supportDaily.length.toLocaleString()}</span>
              </div>
              <div className="pt-2 border-t border-gray-100 text-xs text-gray-500">
                Units: <span className="font-medium text-gray-700">{dashboard?.meta?.units || 'cents'}</span> · Tax rate:{' '}
                <span className="font-medium text-gray-700">{dashboard?.meta?.tax_rate ?? 0.13}</span>
              </div>
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel
            title="Revenue trend"
            subtitle="GMV vs platform revenue (with completed orders)"
            right={loading ? <div className="text-xs font-semibold text-gray-500">Loading…</div> : null}
          >
            {financeChartData.length ? (
              <div className="h-[280px] sm:h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={financeChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={formatYYYYMMDDCompact}
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'Completed Orders') return [Number(value).toLocaleString(), name];
                        return [`$${Number(value).toFixed(2)}`, name];
                      }}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="gmv" name="GMV" stroke="#4f46e5" strokeWidth={2} dot={false} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="Platform Revenue"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      name="Completed Orders"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No finance data in the selected range.</div>
            )}
          </Panel>

          <Panel title="Orders by status" subtitle="Stacked counts per day">
            {ordersChartData.length ? (
              <div className="h-[280px] sm:h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ordersChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={formatYYYYMMDDCompact}
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {ordersStatuses.map((status, idx) => (
                      <Bar
                        key={status}
                        dataKey={status}
                        stackId="a"
                        fill={ordersBarColors[idx % ordersBarColors.length]}
                        name={status}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No order status data in the selected range.</div>
            )}
          </Panel>

          <Panel title="Support tickets" subtitle="New vs resolved (plus active end-of-day)">
            {supportChartData.length ? (
              <div className="h-[280px] sm:h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={supportChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={formatYYYYMMDDCompact}
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="newTickets"
                      name="New tickets"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="resolvedTickets"
                      name="Resolved tickets"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="activeTickets"
                      name="Active (end of day)"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No support ticket data in the selected range.</div>
            )}
          </Panel>

          <Panel title="Notes" subtitle="How to interpret these graphs">
            <div className="space-y-3 text-sm text-gray-700">
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <div className="font-semibold text-gray-900">Amounts</div>
                <div className="mt-1 text-gray-600">
                  Backend returns integer cents; charts and KPIs use dollars on screen.
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <div className="font-semibold text-gray-900">Timezone</div>
                <div className="mt-1 text-gray-600">
                  Grouping into days uses the timezone you provide. If charts look “shifted”, double-check the timezone.
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <div className="font-semibold text-gray-900">Support</div>
                <div className="mt-1 text-gray-600">
                  Resolved counts and active end-of-day are approximations using ticket updated timestamps.
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </main>
    </div>
  );
}

export default Home;
